/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {login} from '../core/_requests'
import {KTIcon, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {AuthModel, UserModel} from '../core/_models'
import {setTokenInStorage} from '../core/AuthHelpers'
import {setUserInStorage, setReportesInStorage} from '../core/UserHelpers'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato de email incorrecto')
    .min(3, 'Mínimo 3 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('El email es requerido'),
  password: Yup.string()
    .min(3, 'Mínimo 3 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('La contraseña es requerida'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting, setFieldTouched}) => {
      setLoading(true)
      try {
        const {data: loginResponse} = await login(values.email, values.password)
        
        if (loginResponse.code === 'OK') {
          const auth: AuthModel = {
            api_token: loginResponse.api_token
          }
          setTokenInStorage(loginResponse.api_token)
          
          const user: UserModel = {
            id: loginResponse.id,
            email: loginResponse.email,
            first_name: loginResponse.first_name,
            last_name: loginResponse.last_name,
            fullname: `${loginResponse.first_name} ${loginResponse.last_name}`,
            is_admin: loginResponse.is_admin,
            supervisor_id: loginResponse.supervisor_id,
            supervisor_name: loginResponse.supervisor_name,
            created_at: loginResponse.created_at,
            updated_at: loginResponse.updated_at,
            email_verified_at: loginResponse.email_verified_at,
            cambiar_password: loginResponse.cambiar_password,
            roles: loginResponse.roles,
            funcionalidades: loginResponse.funcionalidades,
            reportes: loginResponse.reportes || [],
            auth
          }
          
          saveAuth(auth)
          setCurrentUser(user)
          setUserInStorage(user)
          if (loginResponse.reportes && loginResponse.reportes.length > 0) {
            setReportesInStorage(loginResponse.reportes)
          }
        } else {
          setLoading(false)
          setStatus('Nombre de usuario o contraseña incorrectos')
          setFieldTouched('email', false)
          setFieldTouched('password', false)
        }
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('Los datos de inicio de sesión son incorrectos')
        setSubmitting(false)
        setLoading(false)
        setFieldTouched('email', false)
        setFieldTouched('password', false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        {/* Logo (Opcional, si quieres mostrarlo aquí también) */}
        <div className='mb-5'>
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/logos/logo-rojo.svg')}
            className='h-60px app-sidebar-logo-default'
          />
        </div>
        
        <h1 className='text-dark fw-bolder mb-3 fs-1'>Bienvenido a CTMA</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Ingresa tus credenciales para acceder al panel de gestión
        </div>
      </div>
      {/* end::Heading */}

      {/* begin::Alerts */}
      {formik.status && (
        <div className='alert alert-dismissible bg-light-danger d-flex flex-column flex-sm-row p-5 mb-10'>
          <KTIcon iconName='message-text-2' className='fs-2hx text-danger me-4 mb-5 mb-sm-0' />
          <div className='d-flex flex-column pe-0 pe-sm-10'>
            <h5 className='mb-1'>Error de Acceso</h5>
            <span>{formik.status}</span>
          </div>
        </div>
      )}
      {/* end::Alerts */}

      {/* begin::Form group Email */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bold text-dark'>Email</label>
        <div className='input-group input-group-solid'>
          <span className='input-group-text bg-light-primary border-0'>
            <KTIcon iconName='sms' className='fs-2 text-primary' />
          </span>
          <input
            placeholder='ejemplo@ctma.com.uy'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {'is-valid': formik.touched.email && !formik.errors.email}
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
        </div>
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container invalid-feedback'>
            <div data-field='email' data-validator='notEmpty'>
              {formik.errors.email}
            </div>
          </div>
        )}
      </div>
      {/* end::Form group Email */}

      {/* begin::Form group Password */}
      <div className='fv-row mb-3'>
        <div className='d-flex justify-content-between mt-n5'>
          <div className='d-flex flex-stack mb-2'>
            <label className='form-label fw-bold text-dark fs-6 mb-0'>Contraseña</label>
          </div>
        </div>
        
        <div className='input-group input-group-solid'>
          <span className='input-group-text bg-light-primary border-0'>
            <KTIcon iconName='key' className='fs-2 text-primary' />
          </span>
          <input
            type='password'
            placeholder='Ingresa tu contraseña'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.password && formik.errors.password},
              {'is-valid': formik.touched.password && !formik.errors.password}
            )}
          />
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container invalid-feedback'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group Password */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />
        <Link to='/auth/forgot-password' className='link-primary'>
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label fs-4 fw-bold'>Ingresar al Sistema</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Verificando credenciales...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  )
}
